import React from "react";
import { Box, Stack, Typography, Chip } from "@mui/material";
import colors from "../../../../../../utils/colors";
import { ReactComponent as SignaturewafIcon } from "../../../../../../vendor/_new/icon/sites/signature_waf.svg";

const SmartParseIcon = ({ color }) => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4 6H20M4 10H20M4 14H14M4 18H10"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M16 14L18 16L22 12"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const WAF_INFO = {
  smartparse: {
    title: "Layerd AI Smart Parse",
    subtitle: "With Advanced AI Reasoning",
    description: "Intelligent request parsing and validation. Detects SQL injection, XSS, and command injection with configurable sensitivity.",
    icon: "smartparse",
    accentColor: colors.primary[300],
    tagColor: colors.primary[300],
    tagBg: colors.primary[100],
  },
  signature: {
    title: "Signature WAF",
    subtitle: "Rule-Based Detection",
    description: "Fast protection using Layerd's curated signature database against known attack patterns.",
    icon: "signature",
    accentColor: colors.primary[300],
    tagColor: colors.primary[300],
    tagBg: colors.primary[100],
  },
};

const getIcon = (type, enabled, size = 32) => {
  const info = WAF_INFO[type];
  const color = enabled ? info.accentColor : colors.neutral[400];
  
  switch (type) {
    case "smartparse":
      return <SmartParseIcon color={color} />;
    case "signature":
      return <SignaturewafIcon style={{ width: size, height: size, color }} />;
    default:
      return null;
  }
};

const WAFStatusCard = ({ type, enabled, settings, onEdit, variant = "standard" }) => {
  const info = WAF_INFO[type];
  const isFeatured = variant === "featured";

  return (
    <Box
      sx={{
        position: "relative",
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        border: `1px solid ${enabled ? info.accentColor : colors.neutral[200]}`,
        p: isFeatured ? 5 : 4,
        height: "100%",
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: info.accentColor,
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        },
      }}
    >
      {isFeatured && (
        <Chip
          label="Core Product"
          size="small"
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            fontSize: 11,
            fontWeight: 500,
            backgroundColor: info.tagBg,
            color: info.tagColor,
            border: `1px solid ${info.tagColor}`,
            height: 22,
          }}
        />
      )}

      <Stack spacing={3} sx={{ height: "100%" }}>
        <Stack direction="row" spacing={3} alignItems="flex-start">
          <Box
            sx={{
              width: isFeatured ? 56 : 48,
              height: isFeatured ? 56 : 48,
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: enabled ? info.tagBg : colors.neutral[100],
              flexShrink: 0,
            }}
          >
            {getIcon(type, enabled, isFeatured ? 32 : 28)}
          </Box>
          <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography
                sx={{
                  fontSize: isFeatured ? 20 : 16,
                  fontWeight: 600,
                  lineHeight: 1.3,
                  color: enabled ? info.accentColor : colors.neutral[600],
                }}
              >
                {info.title}
              </Typography>
              {info.legacyBadge && (
                <Chip
                  label="Legacy"
                  size="small"
                  sx={{
                    fontSize: 11,
                    fontWeight: 500,
                    backgroundColor: colors.primary[100],
                    color: colors.primary[300],
                    height: 20,
                  }}
                />
              )}
            </Stack>
            <Typography
              sx={{
                fontSize: 13,
                color: colors.neutral[500],
              }}
            >
              {info.subtitle}
            </Typography>
          </Stack>
        </Stack>

        <Typography
          sx={{
            fontSize: 14,
            lineHeight: 1.6,
            color: colors.neutral[700],
            flex: 1,
          }}
        >
          {info.description}
        </Typography>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            pt: 3,
            borderTop: `1px solid ${colors.neutral[200]}`,
          }}
        >
          <Stack direction="row" spacing={4}>
            {settings && Object.entries(settings).map(([key, value], index) => (
              <Stack key={index} spacing={0.5}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 500,
                    color: colors.neutral[500],
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {key}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 600,
                    color: colors.neutral[900],
                  }}
                >
                  {typeof value === "boolean" ? (value ? "On" : "Off") : value}
                </Typography>
              </Stack>
            ))}
          </Stack>
          
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip
              label={enabled ? "Active" : "Inactive"}
              size="small"
              sx={{
                fontSize: 12,
                fontWeight: 500,
                backgroundColor: enabled ? colors.primary[100] : colors.neutral[100],
                color: enabled ? colors.primary[300] : colors.neutral[500],
                border: `1px solid ${enabled ? colors.primary[200] : colors.neutral[300]}`,
                height: 26,
              }}
            />
            <Typography
              onClick={onEdit}
              sx={{
                fontSize: 14,
                fontWeight: 500,
                color: colors.primary[300],
                cursor: "pointer",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              Configure
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

export default WAFStatusCard;
